import { AccountId, CefiInfoTx, Order, Transaction, TxStatus, TxType, WalletType } from "@cede/types";
import { DEFAULT_UNDEFINED_STRING } from "../constants";
import { Precise } from "../math/precise";
import { getBaseToken, getQuoteToken } from "../trade/parsePairSymbol";

export const orderStatusToTransactionStatus = (status: Order["status"]): TxStatus => {
  return (() => {
    switch (status) {
      case "open":
        return TxStatus.PENDING;
      case "closed":
        return TxStatus.OK;
      case "canceled":
        return TxStatus.CANCELLED;
      default:
        return TxStatus.CANCELLED; // it can only be 'expired' or 'rejected' according to CCXT
    }
  })();
};

export function orderToTransactionMapper(
  order: Order & { accountId: AccountId; exchangeId: string },
): Transaction<CefiInfoTx, CefiInfoTx> {
  const status = orderStatusToTransactionStatus(order.status);
  const type: TxType = "order";
  const filled = order.type === "market" ? order.filled : order.amount;
  // cost is equal to 0 when order is opened so we need to guess it
  const cost = Precise.stringMul(String(order.price), String(filled));
  const from: CefiInfoTx = {
    account: {
      exchangeId: order.exchangeId,
      accountId: order.accountId,
    },
    wallet: {
      walletType: WalletType.SPOT,
      depositAddress: "unknown",
    },
    transaction: {
      amount: order.side === "sell" ? filled : Number(cost),
      tokenSymbol:
        (order.side === "sell" ? getBaseToken(order.pairSymbol) : getQuoteToken(order.pairSymbol)) ||
        DEFAULT_UNDEFINED_STRING,
      transactionId: order.id,
      fee: {
        amount: order.fee?.amount ?? 0,
        tokenSymbol: order.fee?.tokenSymbol ?? getBaseToken(order.pairSymbol),
      },
      status,
      timestamp: order.timestamp,
    },
  };
  const to: CefiInfoTx = {
    account: {
      exchangeId: order.exchangeId,
      accountId: order.accountId,
    },
    wallet: {
      walletType: WalletType.SPOT,
      depositAddress: "unknown",
    },
    transaction: {
      // cost is equal to 0 when order is opened
      amount: order.side === "sell" ? Number(cost) : filled,
      tokenSymbol:
        (order.side === "sell" ? getQuoteToken(order.pairSymbol) : getBaseToken(order.pairSymbol)) ||
        DEFAULT_UNDEFINED_STRING,
      transactionId: order.id,
      fee: {
        amount: order.fee?.amount ?? 0,
        tokenSymbol: order.fee?.tokenSymbol ?? getQuoteToken(order.pairSymbol),
      },
      status,
      timestamp: order.timestamp,
    },
  };

  return {
    from,
    to,
    type,
    pairSymbol: order.pairSymbol,
  };
}
